import { useDispatch } from 'react-redux';
import { ERROR_DIALOG } from '../constants/errorDialogConstants';
import { openErrorDialog } from '../slices/errorDialogSlice/errorDialogSlice';
import { administrationErrorMessages } from './administrationErrorMessages';
import { formatString } from './stringFormatHelper';

const errorMessages = {
  login: {
    too_many_attempts: 'Zu viele Login-Versuche. Versuche es später noch einmal',
    unauthorized: 'Nutzer nicht zugelassen',
    validate_mail_required: 'E-Mail wird benötigt',
    validate_mail_format: 'Ungültige E-Mail Adresse',
    validate_mobile_number_required: 'Handynummer wird benötigt',
    validate_mobile_number_format: 'Ungültige Handynummer',
    validate_default_required: 'Handynummer oder E-Mail wird benötigt',
    validate_default_format: 'Ungültige Handynummer oder E-Mail',
    validate_verification_code_required: 'Der Verifizierungscode ist leer.',
    validate_verification_code_format: 'Der Verifizierungscode sollte aus 6 Ziffern bestehen.',
    unknown_mail:
      'Es konnte kein User mit dieser E-Mail gefunden werden. Falls Sie noch keinen Account haben und sich registrieren möchten, wenden Sie sich an Ihren Digital Coach oder Außendienstmitarbeiter Ihres Großhändlers.',
    unknown_phone_number:
      'Es konnte kein User mit dieser Handynummer gefunden werden. Falls Sie noch keinen Account haben und sich registrieren möchten, wenden Sie sich an Ihren Digital Coach oder Außendienstmitarbeiter Ihres Großhändlers.',
    unknown_default:
      'Es konnte kein User mit dieser E-Mail oder Handynummer gefunden werden. Falls Sie noch keinen Account haben und sich registrieren möchten, wenden Sie sich an Ihren Digital Coach oder Außendienstmitarbeiter Ihres Großhändlers.',
    wrong_verification_code: 'Der eingegebene Verifizierungs-Code ist ungültig',
    connection_not_enabled: 'Es ist ein Fehler beim Login-Dienst aufgetreten.',
    default: 'Login-Fehler. Versuche es später erneut',
  },
  passwordReset: {
    default: 'Beim zurücksetzen ist ein Fehler aufgetreten. Bitte versuche es später erneut.',
  },
  load_media: {
    default:
      'Datei konnte nicht heruntergeladen werden. Bitte überprüfen Sie ihre Netzwerverbindung und versuchen Sie es später erneut.',
  },
  files: {
    wrongFileSize: 'Die Datei %s überschreitet die maximale Größe von %s MB.',
    wrongFileType: 'Die Datei %s hat nicht einen der folgenden Dateitypen: %S.',
  },
  validate_house_custom_message: {
    default:
      'Beim validieren der Nachricht ist ein Fehler aufgetreten. Bitte korrigieren Sie den Text und versuchen Sie es erneut.',
  },
  fetch_channels: {
    default: 'Beim Laden der Channel is ein Fehler aufgetreten',
  },
  delete_company: {
    execution_in_progress: 'Der Kunde wird derzeit archiviert, keine Änderungen möglich.',
  },
  delete_company_user: {
    execution_in_progress: 'Der Nutzer wird derzeit archiviert, keine Änderungen möglich.',
  },
  delete_connection_member: {
    last_connection_member: 'Wenn nur eine ONLINE PLUS-Verbindung besteht, kann diese nicht entfernt werden.',
    default: 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.',
  },
  add_erp_customer_user: {
    existing: 'Dieser Craftsman hat schon eine Verbindung im selben Haus.',
    default: 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.',
  },
  delete_channel_member: {
    last_member_cannot_be_deleted: 'Das letzte Mitglied kann nicht aus einem Chat entfernt werden.',
    main_contact_cannot_be_deleted: 'Der Hauptkontakt kann nicht gelöscht werden.',
    default: 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.',
  },
  leaveSalesSpace: {
    default: 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.',
  },
  joinSalesSpace: {
    default: 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.',
  },
  create_channel_member: {
    user_not_in_assignment: 'Benutzer nicht der Kommunikationszuweisung.',
    default: 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.',
  },
  auth_error: {
    token_expired: 'Ihr Token ist abgelaufen. Bitte schließen Sie das Fenster und öffnen Sie es erneut.',
    default: 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.',
  },
  create_channel: {
    forbidden: 'Der Channel konnte nicht erstellt werden. Keine Berechtigung',
    no_common_house:
      'Der Channel konnte nicht erstellt werden. Bitte wählen Sie nur Nutzer aus, die dem gleichen Haus zugeordnet sind.',
    default: 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.',
  },
  agent_user_service: {
    agent_email_already_in_use: 'Ein Mitarbeiter mit dieser E-Mail-Adresse existiert bereits.',
    default: 'Fehler beim Aktualisieren.',
  },
  chat: {
    default: 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.',
  },
  ets: {
    create_ticket: {
      default: 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.',
      no_connection_id: 'Es können keine Tickets für gelöschte Nutzer angelegt werden.',
    },
  },
  value_error: {
    any_str: {
      max_length: 'Die Nachricht konnte nicht gesendet werden, da die maximale Anzahl von Zeichen überschritten ist.',
    },
  },
};

// added seperately to be type-safe instead of using getter function
const DEFAULT_ERROR_MESSAGE = 'Es ist ein Fehler aufgetreten, bitte probieren Sie es später nochmal.';

// @ts-expect-error refactor error messages
const getErrorMsg = (code, ...args): string | undefined => {
  if (!code) {
    return undefined;
  }

  const codeParts = code.split('.');
  const message = codeParts.reduce(
    // @ts-expect-error refactor error messages
    (red, key) => {
      return red?.[key];
    },
    { ...errorMessages, ...administrationErrorMessages },
  );

  return message && message.length > 0 ? formatString(message, ...args) : undefined;
};

// custom error constructor to be able to add the error code in the name field
const throwError = (message: string, name: string) => {
  const error = new Error(message);
  error.name = name;
  throw error;
};

interface UseHandleQueryErrorProps {
  errorDialog: keyof typeof ERROR_DIALOG;
  // TODO infer error type from backend
  error: {
    error_code: string;
    description: string;
  };
}

const useHandleQueryError = () => {
  const dispatch = useDispatch();
  return ({ errorDialog, error }: UseHandleQueryErrorProps) => {
    if (!error) return;
    // must_upgrade errors are handled centrally in the ApiProvider
    if (error.error_code?.startsWith('must_upgrade')) return;
    const errorMessageFromCode = getErrorMsg(error.error_code);
    const defaultErrorMessage = getErrorMsg('chat.default');
    const errorMessageFromApiResonse = error.description;
    const errorMessage = errorMessageFromCode || errorMessageFromApiResonse || defaultErrorMessage;
    if (errorDialog !== ERROR_DIALOG.NONE) {
      dispatch(
        openErrorDialog({
          dialogErrorMsg: errorMessage,
          dialogErrorCode: error.error_code,
          errorDialog,
        }),
      );
    }
    throw new Error(errorMessage);
  };
};

export { DEFAULT_ERROR_MESSAGE, getErrorMsg, throwError, useHandleQueryError };
